import escapeRegexp from 'escape-string-regexp'
import { merge } from 'lodash'
import createDebug from 'debug'

const debug = createDebug('faceted')

const stringLowerCase = value =>
  typeof value === 'string' ? value.trim().toLowerCase() : value

const zeroCounts = counts =>
  Object.keys(counts).reduce((collector, property) => {
    collector[property] = Object.keys(counts[property]).reduce(
      (collector, value) => {
        collector[value] = 0
        return collector
      },
      {}
    )
    return collector
  }, {})

const buildProcessors = properties =>
  properties.reduce((collection, property) => {
    if (typeof property === 'string') {
      collection.set(String(property), stringType(property))
    } else {
      collection.set(String(property), property)
    }
    return collection
  }, new Map())

const faceted = ({ data, properties }) => {
  const propertyProcessors = buildProcessors(properties)

  const filterItems = (data, filter) => {
    return data.filter(item => {
      return Object.keys(filter).every(propName => {
        const processor = propertyProcessors.get(propName)

        if (!processor) return

        if (Array.isArray(filter[propName])) {
          const iterator =
            processor.operator === 'AND'
              ? filter[propName].every.bind(filter[propName])
              : filter[propName].some.bind(filter[propName])
          return (
            filter[propName].length === 0 ||
            iterator(processor.compare(item[propName], item))
          )
        } else {
          return processor.compare(item[propName], item)(filter[propName])
        }
      })
    })
  }

  const countValueUsage = (data, propName) =>
    data.reduce((collector, item) => {
      if (item[propName]) {
        const value = stringLowerCase(item[propName])

        const add = val => {
          if (!collector[val]) {
            collector[val] = 1
          } else {
            collector[val] += 1
          }
        }

        if (Array.isArray(value)) {
          value.forEach(add)
        } else {
          add(value)
        }
      }
      return collector
    }, {})

  const applyFilterAdjustments = (counts, data, filter) => {
    const filterCounts = {}
    Object.keys(filter).forEach(filterProperty => {
      const differentFilter = { ...filter, [filterProperty]: undefined }
      delete differentFilter[filterProperty]
      const results = filterItems(data, differentFilter)
      filterCounts[filterProperty] = countValueUsage(results, filterProperty)
    })
    return { ...counts, ...filterCounts }
  }

  const count = data =>
    data.reduce((properties, item) => {
      Object.keys(item).forEach(propName => {
        const processor = propertyProcessors.get(propName)
        if (!processor) return
        if (!properties[propName]) {
          properties[propName] = {}
        }
        const value = processor.process(item[propName])

        const add = val => {
          if (!properties[propName][val]) {
            properties[propName][val] = 1
          } else {
            properties[propName][val] += 1
          }
        }

        if (Array.isArray(value)) {
          value.forEach(add)
        } else {
          add(value)
        }
      })
      return properties
    }, {})

  let currentFilter = {}
  let counts = count(data)
  const baseCounts = zeroCounts(counts)
  let filteredCounts = { ...counts }

  let results = [...data]

  const setFilter = filter => {
    const start = Date.now()
    const names = properties.map(p => p.toString())
    const filterableProperties = Object.entries(filter)
      .filter(([key]) => names.includes(key))
      .reduce((obj, [key, val]) => Object.assign(obj, { [key]: val }), {})

    currentFilter = { ...filterableProperties }
    debug('filter', filterableProperties)
    results = filterItems(data, currentFilter)
    debug('results', results)
    const filterCounts = applyFilterAdjustments(
      count(results, propertyProcessors),
      data,
      filterableProperties
    )

    filteredCounts = merge({}, baseCounts, filterCounts)
    debug('filterCounts', filteredCounts)
    // currentFilter = Object.keys(currentFilter).reduce((collector, propName) => {
    //   if (Array.isArray(currentFilter[propName])) {
    //     collector[propName] = currentFilter[propName]
    //       .filter(value => {
    //         const lowerCaseValue = stringLowerCase(value)
    //         return filteredCounts[propName][lowerCaseValue] && filteredCounts[propName][lowerCaseValue] > 0
    //       })
    //   } else {
    //     collector[propName] = currentFilter[propName]
    //   }
    //   return collector
    // }, {})
    // debug('currentFilter', currentFilter)
    debug('time', Date.now() - start)
  }

  return {
    counts,
    getFilteredCount: () => filteredCounts,
    setFilter,
    getFilter: () => currentFilter,
    getResults: () => results
  }
}

export const stringType = name => ({
  toString: () => name,
  compare: a => {
    const value = stringLowerCase(a)
    return b => value === stringLowerCase(b)
  },
  process: stringLowerCase
})

export const numberType = name => ({
  toString: () => name,
  process: f => f,
  compare: a => {
    const value = Number(a)
    return b => value === Number(b)
  }
})

export const multiType = name => ({
  toString: () => name,
  process: f => f,
  compare: a => {
    return b => {
      return a.includes(b)
    }
  }
})

const rounder = size => n => Math.round(n / size) * size

export const quantizeType = (name, size) => {
  const round = rounder(size)
  return {
    toString: () => name,
    process: value => round(value),
    compare: a => {
      const value = round(Number(a))
      return b => value === Number(b)
    }
  }
}

export const rangeType = name => {
  return {
    toString: () => name,
    process: f => f,
    compare: a => {
      const value = Number(a)
      return b => {
        const min = b.min || -Infinity
        const max = b.max || Infinity
        return value >= min && value <= max
      }
    }
  }
}

export const textType = name => ({
  toString: () => name,
  operator: 'AND',
  compare: a => {
    const value = stringLowerCase(a)
    return b => {
      const regex = new RegExp(escapeRegexp(b), 'i')
      return regex.test(value)
    }
  },
  process: stringLowerCase
})

export default faceted
